import React, { lazy, Suspense, useState, useMemo, useEffect } from "react";
import { TabItem, TabControl } from "@abb/abb-common-ux-react";
import SingleLineDiagram from "../Single-Line-Diagram/single-line-diagram";
import { isWNTModuleVisible, getUIConfig } from "../../../utils/uiConfigUtils";
import "./wnt-navbar.css";

const DigitalTwin = lazy(() => import("../Digital-Twin/digital-twin"));
const GeographicalMap = lazy(() => import("../Geographical-Map/geographical-map"));
const SolarOutputPrediction = lazy(() => import("../Solar-Output-Prediction/solar-output-prediction"));

function WNTNavBar() {
  const [activeTab, setActiveTab] = useState(0);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Load UI configuration from DynamoDB on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        console.log('🔄 WNT: Loading UI Configuration from DynamoDB...');
        await getUIConfig(); // This fetches from DynamoDB and caches in localStorage
        setConfigLoaded(true);
        console.log('✅ WNT: Configuration loaded');
      } catch (error) {
        console.error('❌ WNT: Error loading configuration:', error);
        setConfigLoaded(true); // Continue with cached config
      }
    };
    
    loadConfig();

    // Listen for UI configuration updates from Edit Configuration page
    const handleConfigUpdate = () => {
      console.log('🔄 WNT: Configuration updated, reloading...');
      setConfigLoaded(false);
      loadConfig();
    };

    window.addEventListener('uiConfigUpdated', handleConfigUpdate);
    
    return () => {
      window.removeEventListener('uiConfigUpdated', handleConfigUpdate);
    };
  }, []);

  const tabConfig = useMemo(() => {
    // Don't render tabs until config is loaded
    if (!configLoaded) {
      return [];
    }

    // Use default tabs with visibility filtering from Edit Configuration
    const allTabs = [
      {
        id: "sld",
        moduleKey: "singleLineDiagram",
        label: "Single Line Diagram",
        component: <SingleLineDiagram />,
      },
      {
        id: "geo",
        moduleKey: "geographicalMap",
        label: "Geographical Map",
        component: (
          <Suspense fallback={<div className="lazy-loader">Loading Geographical Map...</div>}>
            <GeographicalMap isActive={activeTab === 1} />
          </Suspense>
        ),
        customClass: "geo-tab-item",
      },
      {
        id: "dt",
        moduleKey: "digitalTwin",
        label: "Digital Twin",
        component: (
          <Suspense fallback={<div className="lazy-loader">Loading Digital Twin...</div>}>
            <DigitalTwin isActive={activeTab === 2} />
          </Suspense>
        ),
      },
      {
        id: "solar",
        moduleKey: "solarOutputPrediction",
        label: "Solar Output Prediction",
        component: (
          <Suspense fallback={<div className="lazy-loader">Loading Solar Output Prediction...</div>}>
            <SolarOutputPrediction isActive={activeTab === 3} />
          </Suspense>
        ),
      }
    ];

    // Filter tabs based on UI configuration from Edit Configuration page
    const visibleTabs = allTabs.filter(tab => isWNTModuleVisible(tab.moduleKey));
    
    console.log('🔍 WNT Tabs Visibility Check:', {
      totalTabs: allTabs.length,
      visibleTabs: visibleTabs.length,
      hiddenTabs: allTabs.length - visibleTabs.length,
      visibleTabNames: visibleTabs.map(t => t.label)
    });

    return visibleTabs;
  }, [activeTab, configLoaded]); // Re-run when config is loaded

  const isGeoTab = tabConfig[activeTab]?.id === "geo";

  // Show loading while config is being fetched
  if (!configLoaded) {
    return (
      <div className="wnt-component-container">
        <div className="wnt-header-container">
          <div className="wnt-header-opt">
            <span>Wide Area Network Twin Framework</span>
          </div>
        </div>
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          fontSize: '16px', 
          color: '#666' 
        }}>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Check if there are any visible tabs
  if (tabConfig.length === 0) {
    return (
      <div className="wnt-component-container">
        <div className="wnt-header-container">
          <div className="wnt-header-opt">
            <span>Wide Area Network Twin Framework</span>
          </div>
        </div>
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          fontSize: '16px', 
          color: '#666' 
        }}>
          <p>No WNT modules are currently enabled.</p>
          <p>Please go to Admin Panel → Edit Modules and enable WNT modules, then save the UI Configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wnt-component-container">
      <div className="wnt-header-container">
        <div className="wnt-header-opt">
          <span>Wide Area Network Twin Framework</span>
        </div>
      </div>

      <div className={`wnt-tabs ${isGeoTab ? "geo-tab-active" : ""}`}>
        <TabControl
          type="primary"
          activeTab={activeTab}
          onTabChange={(_, newIndex) => setActiveTab(newIndex)}
        >
          {tabConfig.map(({ id, label, component, customClass }) => (
            <TabItem
              key={id}
              title={label}
              className={customClass || ""}
            >
              {component}
            </TabItem>
          ))}
        </TabControl>
      </div>
    </div>
  );
}

export default WNTNavBar;